import { SexUpper } from 'primary-schema';
import { type SetOptional, type ValueOf } from 'type-fest';
import { z } from 'zod';

export class APIError extends Error {
  payload: unknown;
  status: number;

  constructor(payload: unknown, status: number, statusText: string) {
    super(
      typeof payload === 'object' && payload !== null
        ? JSON.stringify({ ...payload, status })
        : `${status} ${statusText}`
    );
    this.name = 'APIError';
    this.payload = payload;
    this.status = status;
  }
}

export type Nullable<T> = { [P in keyof T]: T[P] | null };

export enum Environment {
  Dev = 'dev',
  Staging = 'staging',
  Demo = 'demo',
  Prod = 'prod',
  Weblocal = 'weblocal',
  Webprod = 'webprod',
  Webbucket = 'webbucket',
}

export const BaseItemSchema = z.object({
  id: z.string(),
  created: z.string(),
  updated: z.string(),
});

export type BaseItem = z.infer<typeof BaseItemSchema>;

export interface BaseEhrPatient {
  ehrInformationId: string;
  patientEhrId: string;
  patientId: string;
  practiceId: string;
  patientRecordId: string;
}

export interface OffsetPaginationResponse<T extends object> {
  results: T[];
  page: number;
  limit: number;
  count: number;
}

export interface PersistentItem {
  isDeleted: boolean;
  deletedTimestamp: Date | null;
}

export interface SuccessResponse {
  success: boolean;
  message?: string;
}

type KeysAccepting<T, U> = NonNullable<{ [K in keyof T]: U extends T[K] ? K : never }[keyof T]>;

// Makes all nullable fields optional
export type NullablePartial<T> = SetOptional<T, KeysAccepting<T, null>>;

export const ZeroBasedSundayDayOfWeek = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
} as const;
export type ZeroBasedSundayDayOfWeek =
  (typeof ZeroBasedSundayDayOfWeek)[keyof typeof ZeroBasedSundayDayOfWeek];

export const Hour = {
  '12 AM': 0,
  '01 AM': 1,
  '02 AM': 2,
  '03 AM': 3,
  '04 AM': 4,
  '05 AM': 5,
  '06 AM': 6,
  '07 AM': 7,
  '08 AM': 8,
  '09 AM': 9,
  '10 AM': 10,
  '11 AM': 11,
  '12 PM': 12,
  '01 PM': 13,
  '02 PM': 14,
  '03 PM': 15,
  '04 PM': 16,
  '05 PM': 17,
  '06 PM': 18,
  '07 PM': 19,
  '08 PM': 20,
  '09 PM': 21,
  '10 PM': 22,
  '11 PM': 23,
} as const;
export type Hour = (typeof Hour)[keyof typeof Hour];

export type Minute =
  | 0
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 7
  | 8
  | 9
  | 10
  | 11
  | 12
  | 13
  | 14
  | 15
  | 16
  | 17
  | 18
  | 19
  | 20
  | 21
  | 22
  | 23
  | 24
  | 25
  | 26
  | 27
  | 28
  | 29
  | 30
  | 31
  | 32
  | 33
  | 34
  | 35
  | 36
  | 37
  | 38
  | 39
  | 40
  | 41
  | 42
  | 43
  | 44
  | 45
  | 46
  | 47
  | 48
  | 49
  | 50
  | 51
  | 52
  | 53
  | 54
  | 55
  | 56
  | 57
  | 58
  | 59;

export const Interval = {
  'Every minute': 1,
  'Every 2 minutes': 2,
  'Every 5 minutes': 5,
  'Every 10 minutes': 10,
  'Every 15 minutes': 15,
  'Every half hour': 30,
  'Every hour': 60,
  'Every 2 hours': 120,
  'Every 6 hours': 360,
  'Every 12 hours': 720,
  'Every day': 1440,
} as const;
export type Interval = (typeof Interval)[keyof typeof Interval];

export enum Specialty {
  Acupuncture = 'ACUPUNCTURE',
  AllergyAndImmunology = 'ALLERGY_AND_IMMUNOLOGY',
  Anesthesiology = 'ANESTHESIOLOGY',
  Audiology = 'AUDIOLOGY',
  BariatricSurgery = 'BARIATRIC_SURGERY',
  BehavioralHealth = 'BEHAVIORAL_HEALTH',
  BurnSurgery = 'BURN_SURGERY',
  Cardiology = 'CARDIOLOGY',
  CardiovascularDisease = 'CARDIOVASCULAR_DISEASE',
  CardiothoracicSurgery = 'CARDIOTHORACIC_SURGERY',
  CriticalCare = 'CRITICAL_CARE',
  ColonAndRectalSurgery = 'COLON_AND_RECTAL_SURGERY',
  ConvenientCare = 'CONVENIENT_CARE',
  Dermatology = 'DERMATOLOGY',
  Dental = 'DENTAL',
  Diabetes = 'DIABETES',
  EmergencyMedicine = 'EMERGENCY_MEDICINE',
  Endocrinology = 'ENDOCRINOLOGY',
  Ent = 'ENT',
  FamilyMedicine = 'FAMILY_MEDICINE',
  FamilyPractice = 'FAMILY_PRACTICE',
  Gastroenterology = 'GASTROENTEROLOGY',
  GeneralSurgery = 'GENERAL_SURGERY',
  GeriatricMedicine = 'GERIATRIC_MEDICINE',
  Gynecology = 'GYNECOLOGY',
  HematologyAndOncology = 'HEMATOLOGY_AND_ONCOLOGY',
  Hepatology = 'HEPATOLOGY',
  InfectiousDisease = 'INFECTIOUS_DISEASE',
  InternalMedicine = 'INTERNAL_MEDICINE',
  MedicalGenetics = 'MEDICAL_GENETICS',
  MedicalWeightLoss = 'MEDICAL_WEIGHT_LOSS',
  Neonatology = 'NEONATOLOGY',
  Nephrology = 'NEPHROLOGY',
  NeurologicalSurgery = 'NEUROLOGICAL_SURGERY',
  Neurology = 'NEUROLOGY',
  Neurosurgery = 'NEUROSURGERY',
  NuclearMedicine = 'NUCLEAR_MEDICINE',
  Obgyn = 'OBGYN',
  ObstetricsAndGynecology = 'OBSTETRICS_AND_GYNECOLOGY',
  OccupationalHealth = 'OCCUPATIONAL_HEALTH',
  Ophthalmology = 'OPHTHALMOLOGY',
  Optometry = 'OPTOMETRY',
  OralAndMaxillofacialSurgery = 'ORAL_AND_MAXILLOFACIAL_SURGERY',
  Orthopedics = 'ORTHOPEDICS',
  Other = 'OTHER',
  Otolaryngology = 'OTOLARYNGOLOGY',
  PainMedicine = 'PAIN_MEDICINE',
  PainManagement = 'PAIN_MANAGEMENT',
  PalliativeCare = 'PALLIATIVE_CARE',
  Pathology = 'PATHOLOGY',
  PediatricSurgery = 'PEDIATRIC_SURGERY',
  Pediatrics = 'PEDIATRICS',
  Perinatology = 'PERINATOLOGY',
  PhysicalMedicineAndRehabilitation = 'PHYSICAL_MEDICINE_AND_REHABILITATION',
  PlasticSurgery = 'PLASTIC_SURGERY',
  Podiatry = 'PODIATRY',
  PrimaryCare = 'PRIMARY_CARE',
  Psychiatry = 'PSYCHIATRY',
  Psychology = 'PSYCHOLOGY',
  Pulmonology = 'PULMONOLOGY',
  RadiationOncology = 'RADIATION_ONCOLOGY',
  Radiology = 'RADIOLOGY',
  Rheumatology = 'RHEUMATOLOGY',
  SleepMedicine = 'SLEEP_MEDICINE',
  SportsMedicine = 'SPORTS_MEDICINE',
  StudentMentalHealthAndCounseling = 'STUDENT_MENTAL_HEALTH_AND_COUNSELING',
  ThoracicSurgery = 'THORACIC_SURGERY',
  Toxicology = 'TOXICOLOGY',
  TransplantSurgery = 'TRANSPLANT_SURGERY',
  TraumaSurgery = 'TRAUMA_SURGERY',
  UrgentCare = 'URGENT_CARE',
  Urology = 'UROLOGY',
  UroGynecology = 'URO_GYNECOLOGY',
  VascularSurgery = 'VASCULAR_SURGERY',
}

export enum Timezone {
  UTC = 'UTC',
  Honolulu = 'Pacific/Honolulu',
  Anchorage = 'America/Anchorage',
  LosAngeles = 'America/Los_Angeles',
  Phoenix = 'America/Phoenix',
  Denver = 'America/Denver',
  Chicago = 'America/Chicago',
  NewYork = 'America/New_York',
}

export enum TimingUnit {
  Minute = 'MINUTES',
  Hour = 'HOURS',
  Day = 'DAYS',
  BusinessDay = 'BUSINESS_DAYS',
  Week = 'WEEKS',
  Month = 'MONTHS',
  Year = 'YEARS',
}

export enum DayPeriod {
  Am = 'AM',
  Pm = 'PM',
}

export enum PhoneNumberKind {
  Cell = 'CELL',
  Work = 'WORK',
  Home = 'HOME',
}

export enum ModificationStatus {
  Added = 'ADDED',
  Removed = 'REMOVED',
  Unmodified = 'UNMODIFIED',
  Updated = 'UPDATED',
}

export enum AgeGroup {
  YOUNG = '<18',
  ADULT = '18-65',
  SENIOR = '65+',
}

// NOTE: web/vivaa-api/src/models/previsit.ts > MaritalStatus has more options.
export enum MaritalStatus {
  SINGLE = 'SINGLE',
  MARRIED = 'MARRIED',
}

export enum IncomeGroup {
  LESS_THAN_TEN_THOUSAND = '$10k/yr',
  TEN_THOUSAND_TO_FIFTY_THOUSAND = '$10k-$50k/yr',
  FIFTY_THOUSAND_TO_ONE_HUNDRED_THOUSAND = '$50k-$100k/yr',
  ONE_HUNDRED_THOUSAND_TO_TWO_HUNDRED_THOUSAND = '$100k-$200k/yr',
  GREATER_THAN_TWO_HUNDRED_THOUSAND = '$200k/yr+',
}

export const DefaultSexes = [SexUpper.Female, SexUpper.Male, SexUpper.Other];

export enum Gender {
  Female = 'FEMALE',
  Male = 'MALE',
  NonConforming = 'NON_CONFORMING',
  Other = 'OTHER',
}

export enum ProviderNameDisplayOption {
  Provider = 'PROVIDER',
  Practice = 'PRACTICE',
}

export type SortColumn<T> = keyof T;

export const SortOrder = {
  Ascending: 'asc',
  Descending: 'desc',
} as const;
export type SortOrder = ValueOf<typeof SortOrder>;

export enum NullsSortOrder {
  First = 'first',
  Last = 'last',
}

export interface Sort<T> {
  column: SortColumn<T>;
  order: SortOrder;
  nullsOrder?: NullsSortOrder;
}

export type Sorts<T> = Sort<T>[];

type Filter<T extends object> = Partial<{
  [K in keyof T]: T[K] extends string | null ? T[K] | T[K][] : undefined;
}>;

export type FindAll<Entity extends object, Sort extends BaseItem = BaseItem> = Partial<{
  filter: Filter<Entity>;
  sorts: Sorts<Sort>;
  limit: number;
  page: number;
}>;

export const SpecialtyMap: {
  [specialty in Specialty]: string;
} = {
  ACUPUNCTURE: 'Acupuncture',
  ALLERGY_AND_IMMUNOLOGY: 'Allergy and Immunology',
  ANESTHESIOLOGY: 'Anesthesiology',
  AUDIOLOGY: 'Audiology',
  BARIATRIC_SURGERY: 'Bariatric Surgery',
  BEHAVIORAL_HEALTH: 'Behavioral Health',
  BURN_SURGERY: 'Burn Surgery',
  CARDIOLOGY: 'Cardiology',
  CARDIOTHORACIC_SURGERY: 'Cardiothoracic Surgery',
  CARDIOVASCULAR_DISEASE: 'Cardiovascular Disease',
  COLON_AND_RECTAL_SURGERY: 'Colon and Rectal Surgery',
  CONVENIENT_CARE: 'Convenient Care',
  CRITICAL_CARE: 'Critical Care',
  DENTAL: 'Dental',
  DERMATOLOGY: 'Dermatology',
  DIABETES: 'Diabetes',
  EMERGENCY_MEDICINE: 'Emergency Medicine',
  ENDOCRINOLOGY: 'Endocrinology',
  ENT: 'Ears, Nose, and Throat',
  FAMILY_MEDICINE: 'Family Medicine',
  FAMILY_PRACTICE: 'Family Practice',
  GASTROENTEROLOGY: 'Gastroenterology',
  GENERAL_SURGERY: 'General Surgery',
  GERIATRIC_MEDICINE: 'Geriatric Medicine',
  GYNECOLOGY: 'Gynecology',
  HEMATOLOGY_AND_ONCOLOGY: 'Hematology and Oncology',
  HEPATOLOGY: 'Hepatology',
  INFECTIOUS_DISEASE: 'Infectious Disease',
  INTERNAL_MEDICINE: 'Internal Medicine',
  MEDICAL_GENETICS: 'Medical Genetics',
  MEDICAL_WEIGHT_LOSS: 'Medical Weight Loss',
  NEONATOLOGY: 'Neonatology',
  NEPHROLOGY: 'Nephrology',
  NEUROLOGICAL_SURGERY: 'Neurological Surgery',
  NEUROLOGY: 'Neurology',
  NEUROSURGERY: 'Neurosurgery',
  NUCLEAR_MEDICINE: 'Nuclear Medicine',
  OBGYN: 'OBGYN',
  OBSTETRICS_AND_GYNECOLOGY: 'Obstetrics and Gynecology',
  OCCUPATIONAL_HEALTH: 'Occupational Health',
  OPHTHALMOLOGY: 'Ophthalmology',
  OPTOMETRY: 'Optometry',
  ORAL_AND_MAXILLOFACIAL_SURGERY: 'Oral and Maxillofacial Surgery',
  ORTHOPEDICS: 'Orthopedics',
  OTHER: 'Other',
  OTOLARYNGOLOGY: 'Otolaryngology',
  PAIN_MANAGEMENT: 'Pain Management',
  PAIN_MEDICINE: 'Pain Medicine',
  PALLIATIVE_CARE: 'Palliative Care',
  PATHOLOGY: 'Pathology',
  PEDIATRICS: 'Pediatrics',
  PEDIATRIC_SURGERY: 'Pediatric Surgery',
  PERINATOLOGY: 'Perinatology',
  PHYSICAL_MEDICINE_AND_REHABILITATION: 'Physical Medicine and Rehabilitation',
  PLASTIC_SURGERY: 'Plastic Surgery',
  PODIATRY: 'Podiatry',
  PRIMARY_CARE: 'Primary Care',
  PSYCHIATRY: 'Psychiatry',
  PSYCHOLOGY: 'Psychology',
  PULMONOLOGY: 'Pulmonology',
  RADIATION_ONCOLOGY: 'Radiation Oncology',
  RADIOLOGY: 'Radiology',
  RHEUMATOLOGY: 'Rheumatology',
  SLEEP_MEDICINE: 'Sleep Medicine',
  SPORTS_MEDICINE: 'Sports Medicine',
  STUDENT_MENTAL_HEALTH_AND_COUNSELING: 'Student Mental Health and Counseling',
  THORACIC_SURGERY: 'Thoracic Surgery',
  TOXICOLOGY: 'Toxicology',
  TRANSPLANT_SURGERY: 'Transplant Surgery',
  TRAUMA_SURGERY: 'Trauma Surgery',
  URGENT_CARE: 'Urgent Care',
  UROLOGY: 'Urology',
  URO_GYNECOLOGY: 'Uro Gynecology',
  VASCULAR_SURGERY: 'Vascular Surgery',
};

export enum ReviewState {
  Approved = 'APPROVED',
  AutomaticallyApproved = 'AUTOMATICALLY_APPROVED',
  AutomaticallyRejected = 'AUTOMATICALLY_REJECTED',
  NotReviewed = 'NOT_REVIEWED',
  Rejected = 'REJECTED',
}

export enum ExportState {
  Exported = 'EXPORTED',
  NotExported = 'NOT_EXPORTED',
  ExportFailed = 'EXPORT_FAILED',
}

export const InvertedSpecialtyMap: Record<string, string> = {};
for (const [key, value] of Object.entries(SpecialtyMap)) {
  InvertedSpecialtyMap[value] = key;
}

export * as Pagination from './pagination/index.js';
